import type { ClientGPSLocation } from '@DigitaleDoerfer/digitale-doerfer-api';
import type { FC } from 'react';
import { useCallback, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import {
  ActivityIndicator,
  Button,
  IconButton,
  Text
} from 'react-native-paper';

import { CloseIcon } from '@/assets';
import { LocalizationNameSpace } from '@/localization/types/localizationTypes';
import { CurrentLocationButton } from '@/shared/components/location/CurrentLocationButton';
import { getCurrentLocation } from '@/shared/utils/location';
import { useAppTheme } from '@/styles/hooks/useAppTheme';
import type { AppTheme } from '@/styles/theme';

const makeStyles = (theme: AppTheme) =>
  StyleSheet.create({
    searchBar: {
      height: 56,
      borderRadius: 28,
      backgroundColor: theme.colors.surfaceContainerHigh,
      flexDirection: 'row',
      justifyContent: 'space-between',
      alignItems: 'center'
    },
    searchContent: { flex: 1, flexDirection: 'row', alignItems: 'center' },
    searchLabel: { flex: 1, flexGrow: 1, paddingHorizontal: 0 },
    searchButtonContent: { justifyContent: 'flex-start' },
    searchIndicator: { paddingHorizontal: 14 }
  });

type Props = {
  label?: string;
  placeholder: string;
  isLoading?: boolean;
  onLocate: (position: ClientGPSLocation) => void;
  onClear: () => void;
  onPress: () => void;
};

export const GeoSearchBar: FC<Props> = ({
  label,
  placeholder,
  isLoading,
  onLocate,
  onClear,
  onPress
}) => {
  const theme = useAppTheme();
  const styles = useMemo(() => makeStyles(theme), [theme]);
  const { t } = useTranslation(LocalizationNameSpace.GEO_AREA, {
    keyPrefix: 'components.searchBar'
  });

  const [isGPSLoading, setIsGPSLoading] = useState<boolean>(false);

  const handleGetCurrentLocation = useCallback(async () => {
    setIsGPSLoading(true);
    const location = await getCurrentLocation();
    setIsGPSLoading(false);
    if (location) {
      onLocate(location);
    }
  }, [onLocate]);

  return (
    <View style={styles.searchBar}>
      <View style={styles.searchContent}>
        <CurrentLocationButton onPress={handleGetCurrentLocation} iconOnly />
        <Button
          style={styles.searchLabel}
          labelStyle={{ marginHorizontal: 0 }}
          contentStyle={styles.searchButtonContent}
          onPress={onPress}
          rippleColor="transparent"
        >
          <Text
            variant="bodyLarge"
            style={{
              color: label
                ? theme.colors.onSurface
                : theme.colors.onSurfaceVariant
            }}
          >
            {label ?? placeholder}
          </Text>
        </Button>
      </View>
      {isLoading || isGPSLoading ? (
        <ActivityIndicator style={styles.searchIndicator} />
      ) : (
        label && (
          <IconButton
            icon={({ color, size }) => (
              <CloseIcon fill={color} width={size} height={size} />
            )}
            onPress={onClear}
            accessibilityLabel={t('clear')}
            accessibilityHint={t('clearHint')}
          />
        )
      )}
    </View>
  );
};
